import { Stack } from '@chakra-ui/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { Filters } from '#modules/finder/features/posts/components/Filters';
import fixture from '#modules/finder/features/posts/components/Filters/previews/fixture.json';
import { ParameterComparison } from '#modules/finder/features/posts/components/Filters/previews/ParameterComparison';
import { InitialStep } from '#modules/finder/features/posts/components/PostForm/InitialStep';
import { normalizeFilterSelection } from '#modules/finder/features/posts/model/filterSelection';
import {
  buildFinderFilters,
  type FinderFilterValues,
  type PostSource,
  type SortOption,
  type ViewFilter,
} from '#modules/finder/features/posts/model/finderFilters';
import {
  getGamePostCountsQueryOptions,
  getPostsCountQueryOptions,
} from '#modules/finder/features/posts/model/posts';
import { PageHeading, Panel } from '#features/ui';
import { SelectionColors } from '#features/ui/previews/SelectionColors';

/** Real filter components with a frozen dev catalog and isolated RU/EN; no account writes. */
export const Route = createFileRoute('/{-$locale}/_layout/preview/finder-filters')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    previewLocale: search.previewLocale === 'en' ? ('en' as const) : ('ru' as const),
    guest: search.guest === true || search.guest === 'true',
    filled: search.filled === true || search.filled === 'true',
    empty: search.empty === true || search.empty === 'true',
    unknown: search.unknown === true || search.unknown === 'true',
    compare: search.compare === true || search.compare === 'true',
    initial: search.initial === true || search.initial === 'true',
    colors: search.colors === true || search.colors === 'true',
    countState:
      search.countState === 'loading' || search.countState === 'error'
        ? search.countState
        : 'ready',
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ deps }) => {
    const { messages } =
      deps.previewLocale === 'ru'
        ? await import('#features/i18n/locales/ru/messages.po')
        : await import('#features/i18n/locales/en/messages.po');
    return { ...deps, messages };
  },
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
  component: PreviewRoot,
});

function PreviewRoot() {
  const data = Route.useLoaderData();
  const i18n = useMemo(
    () =>
      setupI18n({ locale: data.previewLocale, messages: { [data.previewLocale]: data.messages } }),
    [data.previewLocale, data.messages]
  );
  return (
    <I18nProvider i18n={i18n}>
      <Preview
        key={JSON.stringify([
          data.previewLocale,
          data.guest,
          data.filled,
          data.empty,
          data.unknown,
          data.countState,
          data.compare,
          data.initial,
          data.colors,
        ])}
        {...data}
      />
    </I18nProvider>
  );
}

function Preview({
  previewLocale,
  guest,
  filled,
  empty,
  unknown,
  countState,
  compare,
  initial,
  colors,
}: {
  previewLocale: 'ru' | 'en';
  guest: boolean;
  filled: boolean;
  empty: boolean;
  unknown: boolean;
  countState: string;
  compare: boolean;
  initial: boolean;
  colors: boolean;
}) {
  const data = fixture[previewLocale];
  const settings = compare
    ? data.settings.map((setting, index) => ({
        ...setting,
        values: index < 2 ? setting.values.slice(0, 4) : setting.values,
      }))
    : data.settings;
  const [gameId, setGameId] = useState(empty ? '' : data.game.id);
  const [values, setValues] = useState<FinderFilterValues>(() =>
    unknown
      ? { platforms: ['missing-platform'], gameSettings: { [settings[0].key]: ['missing-option'] } }
      : filled
        ? {
            mic: ['on'],
            platforms: data.game.platformsIds,
            languages: ['ru', 'en', 'es'],
            gameSettings: Object.fromEntries(
              settings.map((setting) => [setting.key, setting.values.map((value) => value.id)])
            ),
          }
        : {}
  );
  const [sort, setSort] = useState<SortOption>('bumpedAt');
  const [viewFilter, setView] = useState<ViewFilter>('all');
  const [postSource, setSource] = useState<PostSource>('all');
  const [client] = useState(() => {
    const client = new QueryClient({
      defaultOptions: {
        queries: { retry: false, staleTime: Infinity, refetchOnWindowFocus: false },
      },
    });
    const games = initial
      ? [
          data.game,
          {
            ...data.game,
            id: 'preview-single',
            name: 'DEV · PC only',
            platformsIds: data.game.platformsIds.slice(0, 1),
          },
          {
            ...data.game,
            id: 'preview-consoles',
            name: 'DEV · Consoles',
            platformsIds: data.game.platformsIds.slice(1),
          },
        ]
      : [data.game];
    client.setQueryData(['games'], games);
    client.setQueryData(
      getGamePostCountsQueryOptions(games.map((game) => game.id)).queryKey,
      Object.fromEntries(games.map((game) => [game.id, empty ? 0 : 17]))
    );
    if (initial) {
      // A separate QA tab exercises the real step with its own tab-local draft, without API writes.
      client.setQueryData(['me'], null);
      client.setQueryData(['my-post-game-ids'], []);
      for (const game of games) client.setQueryData(['can-create-post', game.id], null);
    }
    client.setQueryData(
      ['platforms'],
      data.game.platformsIds.map((id, i) => ({ id, name: ['PC', 'PLAYSTATION', 'XBOX'][i] }))
    );
    client.setQueryData(['gameSettings', data.game.id], settings);
    const languages = new Intl.DisplayNames([previewLocale], { type: 'language' });
    client.setQueryData(
      ['languages'],
      [
        'ru',
        'en',
        'es',
        'de',
        'ar',
        'fr',
        'pt',
        'zh',
        'ja',
        'ko',
        'hi',
        'uk',
        'pl',
        'it',
        'tr',
      ].map((code) => ({
        code,
        name: languages.of(code),
        nativeName: new Intl.DisplayNames([code], { type: 'language' }).of(code),
      }))
    );
    if (countState !== 'ready') {
      // Isolate the count transport too, including refetch when the dialog opens.
      const defaultQueryOptions = client.defaultQueryOptions.bind(client);
      client.defaultQueryOptions = (options) => {
        const resolved = defaultQueryOptions(options);
        if (resolved.queryKey?.[0] === 'posts-count') {
          resolved.queryFn = () =>
            countState === 'loading'
              ? new Promise<never>(() => {})
              : Promise.reject(new Error('Preview count unavailable'));
        }
        return resolved;
      };
    }
    return client;
  });
  // Frozen preview counts must remain fresh while the dialog is inspected.
  if (countState === 'ready')
    client.setQueryData(
      getPostsCountQueryOptions(
        buildFinderFilters({
          ...normalizeFilterSelection(values, empty ? undefined : data.game.platformsIds),
          gameId,
          viewFilter,
        })
      ).queryKey,
      empty ? 0 : 17,
      { updatedAt: Date.now() + 86_400_000 }
    );
  return (
    <QueryClientProvider client={client}>
      <Stack p={{ base: 4, md: 8 }} gap={4}>
        {colors ? (
          <SelectionColors locale={previewLocale} />
        ) : (
          <>
            <PageHeading>{`DEV · ${initial ? 'Listing first step' : 'Finder filters'} · ${previewLocale}`}</PageHeading>
            {initial ? (
              <Panel variant="object" size="md" maxW="500px">
                <InitialStep isGameDisabled={false} onSubmit={() => {}} />
              </Panel>
            ) : (
              <>
                {compare && (
                  <Panel variant="object" size="md" maxW="lg">
                    <ParameterComparison locale={previewLocale} />
                  </Panel>
                )}
                <Panel variant="object" size="md">
                  <Filters
                    initialValues={{ ...values, gameId }}
                    gameSpecificInitialValues={values}
                    onSubmit={({ gameId: nextGameId }) => {
                      setGameId(nextGameId ?? '');
                      setValues({});
                    }}
                    onSettingsSubmit={(next) => setValues(next as FinderFilterValues)}
                    sort={sort}
                    onSortChange={setSort}
                    viewFilter={viewFilter}
                    onViewFilterChange={setView}
                    postSource={postSource}
                    onPostSourceChange={setSource}
                    isAuth={!guest}
                  />
                </Panel>
              </>
            )}
          </>
        )}
      </Stack>
    </QueryClientProvider>
  );
}
